import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, CalendarDays } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { hotel } = useHotel();
  const { isLoggedIn, user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate('/');
  };

  const hotelName = hotel?.hotelName || 'Hotel';
  const logoUrl = hotel?.logoUrl || null;

  const heroPages = ['/', '/rooms', '/explore', '/tickets'];
  const isHeroPage = heroPages.includes(location.pathname);
  const showSolid = isScrolled || !isHeroPage;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const baseNavLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Rooms', path: '/rooms' },
    { name: 'Gallery', path: '/gallery' }
  ];

  let navLinks = [...baseNavLinks];
  if (hotel?.isExploreEnabled) navLinks.push({ name: 'Explore', path: '/explore' });
  if (hotel?.isTicketEnabled) navLinks.push({ name: 'Tickets', path: '/tickets' });

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolid
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2"
            onClick={() => setIsMenuOpen(false)}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={hotelName} className="w-10 h-10 object-contain rounded-lg" />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">
                  {hotelName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <span
              className={`text-2xl font-bold font-playfair transition-colors duration-300 ${
                showSolid ? 'text-gray-900' : 'text-white'
              }`}
            >
              {hotelName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-medium transition-colors duration-300 hover:text-blue-600 ${
                  location.pathname === link.path
                    ? showSolid
                      ? 'text-blue-600'
                      : 'text-white'
                    : showSolid
                      ? 'text-gray-700 hover:text-blue-600'
                      : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </Link>
            ))}
            {isLoggedIn ? (
              <div className="flex items-center gap-3 ml-4">
                <Link to="/my-bookings"
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${showSolid ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}>
                  <CalendarDays size={16} /> My Bookings
                </Link>
                <div className={`flex items-center gap-1.5 text-sm ${showSolid ? 'text-gray-500' : 'text-white/70'}`}>
                  <User size={14} />{user?.name?.split('@')[0]}
                </div>
                <button onClick={handleLogout}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors ${showSolid ? 'text-gray-500 hover:text-red-600' : 'text-white/70 hover:text-white'}`}>
                  <LogOut size={14} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary ml-4">Sign In</Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors duration-300 ${
              showSolid
                ? 'text-gray-700 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
            }`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMenuOpen 
              ? 'max-h-96 opacity-100' 
              : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="bg-white/95 backdrop-blur-md rounded-xl shadow-xl p-6 mb-4">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors duration-300 hover:text-blue-600 ${
                    location.pathname === link.path
                      ? 'text-blue-600'
                      : 'text-gray-700'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              {isLoggedIn ? (
                <>
                  <Link to="/my-bookings" className="font-medium text-gray-700 hover:text-blue-600 flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}>
                    <CalendarDays size={16} /> My Bookings
                  </Link>
                  <button onClick={handleLogout}
                    className="font-medium text-red-600 hover:text-red-700 flex items-center gap-2 text-left">
                    <LogOut size={16} /> Sign Out
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn btn-primary w-full mt-2 text-center"
                  onClick={() => setIsMenuOpen(false)}>
                  Sign In
                </Link>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;